import React, {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { SettingTypeEnum } from '../../enums/SettingTypeEnum';
import { useAuth } from '../../hooks/useAuth';
import { useTags } from '../../hooks/useTags';
import type Tag from '../../interfaces/Tag';
import { FileService } from '../../services/fileApiService';

interface PermissionsContextType {
  receivedFilesCount: number;
  getCountFilesUnread: () => Promise<void>;
  /** After mark-as-read API: lower sidebar Received badge; invalidates in-flight absolute counts. */
  decreaseReceivedUnreadCountBy: (markedCount: number) => void;
  /** After mark-as-unread API: raise sidebar Received badge; invalidates in-flight absolute counts. */
  increaseReceivedUnreadCountBy: (markedCount: number) => void;
  fileSizeLimitUpload: number;
  tags: Tag[];
  isCurrentFolderReadOnly: boolean;
  setIsCurrentFolderReadOnly: (isCurrentFolderReadOnly: boolean) => void;
  setFileSizeLimitUpload: (fileSizeLimitUpload: number) => void;
}

export const PermissionsContext = createContext<PermissionsContextType>({
  getCountFilesUnread: async () => {},
  decreaseReceivedUnreadCountBy: () => {},
  increaseReceivedUnreadCountBy: () => {},
  receivedFilesCount: 0,
  fileSizeLimitUpload: 250,
  tags: [],
  isCurrentFolderReadOnly: false,
  setIsCurrentFolderReadOnly: () => {},
  setFileSizeLimitUpload: () => {},
});

const fileService = new FileService();

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { isLoggedIn, authenticatedUser, isAdminOrContributor } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { tags } = useTags();
  const [receivedFilesCount, setReceivedFilesCount] = useState(0);
  const [fileSizeLimitUpload, setFileSizeLimitUpload] = useState<number>(250);
  const [isCurrentFolderReadOnly, setIsCurrentFolderReadOnly] = useState<boolean>(false);
  // Drop stale /file/countinfilesunread responses after optimistic ± or a newer fetch.
  const unreadCountFetchGenRef = useRef(0);

  /**
   * @description Get the count of files unread.
   */
  const getCountFilesUnread = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }

    const fetchGen = ++unreadCountFetchGenRef.current;
    try {
      const response = await fileService.getCountFilesUnread();
      if (fetchGen !== unreadCountFetchGenRef.current) {
        return;
      }
      const nextCount = typeof response === 'number' ? response : Number(response);
      setReceivedFilesCount(Number.isFinite(nextCount) ? nextCount : 0);
    } catch {
      console.error('Error getting count files unread');
    }
  }, [isLoggedIn]);

  const decreaseReceivedUnreadCountBy = useCallback((markedCount: number) => {
    if (markedCount <= 0) return;
    // In-flight absolute count must not overwrite this optimistic update.
    unreadCountFetchGenRef.current += 1;
    setReceivedFilesCount((prev) => Math.max(0, prev - markedCount));
  }, []);

  const increaseReceivedUnreadCountBy = useCallback((markedCount: number) => {
    if (markedCount <= 0) return;
    unreadCountFetchGenRef.current += 1;
    setReceivedFilesCount((prev) => prev + markedCount);
  }, []);

  /**
   * @description Get the count of files unread on component mount.
   */
  useEffect(() => {
    // Check if location is /file/:id and avoid getting brandingData
    if (location.pathname.includes('/file/')) {
      return;
    }

    getCountFilesUnread().then(() => {});
  }, [getCountFilesUnread, isLoggedIn]);

  useEffect(() => {
    const routesBlacklist = [
      '/files/personal/*',
      '/files/shared/*',
      '/files/trash',
      '/users/*',
      '/groups',
      '/settings/*',
    ];

    const path = location.pathname;
    const isBlacklisted = routesBlacklist.some((pattern) =>
      matchPath({ path: pattern, end: false }, path),
    );

    // Only enforce after the user record is loaded. During auth boot `authenticatedUser`
    // is still null while `isAdminOrContributor` is false — redirecting here would send
    // admin routes to `/` and then login (E2E flake; real users could see the same flash).
    if (isBlacklisted && authenticatedUser != null && !isAdminOrContributor) {
      navigate('/');
    }
  }, [authenticatedUser, isAdminOrContributor, location.pathname, navigate]);

  useEffect(() => {
    if (authenticatedUser?.settings) {
      for (const setting of authenticatedUser.settings) {
        let parsedAdditionalData;
        try {
          if (!setting.additionalData) continue; // Skip if null/undefined/empty
          parsedAdditionalData = JSON.parse(setting.additionalData);
        } catch (err) {
          continue; // Jump to the next setting
        }
        if (parsedAdditionalData?.type === SettingTypeEnum.FileSizeLimit) {
          setFileSizeLimitUpload(parsedAdditionalData.fileSizeLimit);
        }
      }
    }
  }, [authenticatedUser]);

  const contextValue = useMemo(() => {
    return {
      getCountFilesUnread,
      decreaseReceivedUnreadCountBy,
      increaseReceivedUnreadCountBy,
      receivedFilesCount,
      fileSizeLimitUpload,
      tags,
      isCurrentFolderReadOnly,
      setIsCurrentFolderReadOnly,
      setFileSizeLimitUpload,
    };
  }, [
    getCountFilesUnread,
    decreaseReceivedUnreadCountBy,
    increaseReceivedUnreadCountBy,
    receivedFilesCount,
    fileSizeLimitUpload,
    tags,
    isCurrentFolderReadOnly,
    setIsCurrentFolderReadOnly,
    setFileSizeLimitUpload,
  ]);

  return <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>;
};
